import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { motionSafe as motion } from "@/lib/motionSafe";
import { Link } from "react-router-dom";
import { X, Shield, BarChart3, Megaphone, Film } from "lucide-react";
import { useI18n } from "../../i18n";

const STORAGE_KEY = "nl_cookie_consent_v1";
const REOPEN_EVENT = "nl:open-cookie-settings";

// ---- Runtime script loaders (fired only after opt-in) --------------------
const loadPostHog = () => {
  if (window.__nl_posthog_loaded) return;
  window.__nl_posthog_loaded = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src =
            s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
            "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o =
              "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(
                " "
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
  /* eslint-enable */
};

const loadHubSpot = () => {
  if (window.__nl_hubspot_loaded) return;
  if (document.getElementById("hs-script-loader")) return;
  window.__nl_hubspot_loaded = true;
  const s = document.createElement("script");
  s.id = "hs-script-loader";
  s.type = "text/javascript";
  s.async = true;
  s.defer = true;
  s.src = "//js-eu1.hs-scripts.com/149070847.js";
  document.head.appendChild(s);
};

const GA_ID = "G-7ZFFFNW1MM";
const loadGA = () => {
  if (window.__nl_ga_loaded) {
    // Already loaded — just re-grant analytics_storage.
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: "granted" });
    }
    return;
  }
  window.__nl_ga_loaded = true;

  // Grant analytics_storage BEFORE the tag actually initialises so the very
  // first hit is already consented. Ad storage stays denied.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "granted" });
  }

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  // dataLayer + gtag stub are already defined in index.html (Consent Mode
  // default snippet). We only need to fire js + config.
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };
  window.gtag = gtag;
  gtag("js", new Date());
  // send_page_view stays default true — Enhanced Measurement handles
  // subsequent SPA route changes via the browser-history trigger, so we
  // do NOT emit manual page_view events elsewhere.
  gtag("config", GA_ID, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
};

// ---- Cookie cleanup on consent withdrawal ---------------------------------
const _deleteCookie = (name) => {
  const host = window.location.hostname;
  const bare = host.replace(/^www\./, "");
  const domains = ["", host, "." + host, bare, "." + bare];
  const paths = ["/", ""];
  domains.forEach((d) =>
    paths.forEach((p) => {
      const parts = [`${name}=`, "expires=Thu, 01 Jan 1970 00:00:00 GMT"];
      if (p) parts.push("path=" + p);
      if (d) parts.push("domain=" + d);
      document.cookie = parts.join("; ");
    })
  );
};

const purgeCookiesMatching = (patterns) => {
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name && patterns.some((re) => re.test(name))) _deleteCookie(name);
  });
};

const ANALYTICS_COOKIE_PATTERNS = [
  /^_ga$/,
  /^_ga_/,
  /^_gid$/,
  /^_gat/,
  /^_dc_gtm_/,
  /^AMP_/,
  /^AMP_TOKEN$/,
  /^ph_/,      // PostHog
  /^__ph_/,
];

const MARKETING_COOKIE_PATTERNS = [
  /^hubspotutk$/,
  /^__hstc$/,
  /^__hssc$/,
  /^__hssrc$/,
  /^__hs_/,
  /^_hs_/,
  /^messagesUtk$/,
];

// Reads/writes consent from localStorage. Returns null if never decided.
const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeConsent = (c) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...c, timestamp: new Date().toISOString() })
    );
  } catch {}
};

const applyConsent = (c) => {
  if (c.analytics) {
    loadGA();
    loadPostHog();
  }
  if (c.marketing) loadHubSpot();
};

// Public helper for the Footer to reopen the settings.
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(REOPEN_EVENT));
};

const COPY = {
  de: {
    heading: "Wir respektieren Ihre Privatsphäre",
    lead: "Wir nutzen Cookies für essenzielle Funktionen. Analytics, Marketing und externe Medien werden nur mit Ihrer Einwilligung aktiviert.",
    withdraw: "Sie können Ihre Einwilligung jederzeit über die Cookie-Einstellungen widerrufen. Details finden Sie in unserer ",
    privacyLink: "Datenschutzerklärung",
    and: " und im ",
    imprintLink: "Impressum",
    dot: ".",
    acceptAll: "Alle akzeptieren",
    reject: "Ablehnen",
    settings: "Einstellungen",
    close: "Schließen",
    chooseWhich: "Wählen Sie, welche Kategorien Sie erlauben",
    save: "Auswahl speichern",
    clearSimple: "Klar & einfach",
    changeAnytime:
      "Sie können Ihre Einwilligung jederzeit über den Link „Cookie-Einstellungen ändern“ im Footer anpassen oder widerrufen.",
    reqBadge: "Erforderlich",
    analyticsBadge: "Analytics",
    marketingBadge: "Marketing",
    mediaBadge: "Ext. Medien",
    cats: {
      necessary: {
        title: "Strikt notwendige Cookies",
        body:
          "Nötig für Kernfunktionen wie Navigation, Formularsicherheit und Sprachpräferenz. Immer aktiv und nicht deaktivierbar.",
      },
      analytics: {
        title: "Analytics / Statistik",
        body:
          "Hilft uns zu verstehen, was funktioniert (z. B. Seitenaufrufe, Klicks, Herkunft), damit wir die Website verbessern können. Nutzt Google Analytics 4 und PostHog.",
      },
      marketing: {
        title: "Marketing / Tracking",
        body:
          "Wird für die Messung von Kampagnen-Performance und Nachverfolgung von Anfragen im CRM verwendet (z. B. Retargeting).",
      },
      media: {
        title: "Externe Medien",
        body:
          "Aktiviert eingebettete Inhalte (z. B. YouTube-, Vimeo-Videos, Google Maps, Google-Fonts, LinkedIn-Widgets).",
      },
    },
    details: {
      analytics: {
        purpose: "Reichweitenmessung, Verbesserung der Website, Kampagnen-Attribution",
        providers: "Google Analytics 4 (Google Ireland Ltd. / Google LLC, USA) · PostHog Inc. (USA)",
        retention: "Google Analytics: bis 14 Monate · PostHog: bis 12 Monate",
        transfers: "USA — nur mit Ihrer Einwilligung (Art. 49 Abs. 1 lit. a DSGVO)",
      },
      marketing: {
        purpose: "CRM-Tracking von Anfragen, Kampagnen-Messung",
        providers: "HubSpot Ireland Ltd. (EU-Server js-eu1.hs-scripts.com)",
        retention: "13 Monate (Standard-HubSpot-Cookie hubspotutk)",
        transfers: "primär EU; USA möglich — nur mit Ihrer Einwilligung",
      },
      media: {
        purpose: "Anzeige eingebetteter Videos / Karten / Widgets",
        providers: "Google LLC (Fonts, Maps), LinkedIn, YouTube, Vimeo",
        retention: "abhängig vom Anbieter",
        transfers: "möglich (z. B. USA) — nur mit Ihrer Einwilligung",
      },
    },
    labels: {
      purpose: "Zweck",
      providers: "Anbieter (Beispiele)",
      retention: "Speicherdauer",
      transfers: "Drittland-Transfers",
    },
    linksTitle: "Rechtliche Hinweise",
    legalLinks: [
      { label: "Impressum", to: "/impressum" },
      { label: "Datenschutzerklärung", to: "/datenschutz" },
    ],
    note:
      "Hinweis: Erforderliche Cookies werden sofort gesetzt. Alles andere wird ausschließlich nach Ihrem Opt-in aktiviert.",
    changeInFooter: "Cookie-Einstellungen ändern",
  },
  en: {
    heading: "We respect your privacy",
    lead: "We use cookies for essential features. Analytics, marketing and external media are only enabled with your consent.",
    withdraw:
      "You may withdraw your consent at any time via the cookie settings. Please see our ",
    privacyLink: "Privacy Policy",
    and: " and ",
    imprintLink: "Legal Notice",
    dot: " for detailed information.",
    acceptAll: "Accept all",
    reject: "Reject",
    settings: "Settings",
    close: "Close",
    chooseWhich: "Choose which categories you allow",
    save: "Save selection",
    clearSimple: "Clear & simple",
    changeAnytime:
      "You can change or withdraw your consent at any time via the „Change cookie settings“ link in the footer.",
    reqBadge: "Required",
    analyticsBadge: "Analytics",
    marketingBadge: "Marketing",
    mediaBadge: "Ext. media",
    cats: {
      necessary: {
        title: "Strictly necessary cookies",
        body:
          "Needed for core functionality like navigation, form protection or language preferences. Always active and cannot be disabled.",
      },
      analytics: {
        title: "Analytics / Statistics",
        body:
          "Helps us understand what works (e.g. page views, clicks, referrers) so we can improve the site. Uses Google Analytics 4 and PostHog.",
      },
      marketing: {
        title: "Marketing / Tracking",
        body:
          "Used to measure campaign performance and track inquiries in the CRM (e.g. retargeting).",
      },
      media: {
        title: "Third-party embedded content",
        body:
          "Enables embedded content (e.g. YouTube / Vimeo videos, Google Maps, Google Fonts, LinkedIn widgets).",
      },
    },
    details: {
      analytics: {
        purpose: "Audience measurement, improving the website, campaign attribution",
        providers: "Google Analytics 4 (Google Ireland Ltd. / Google LLC, USA) · PostHog Inc. (USA)",
        retention: "Google Analytics: up to 14 months · PostHog: up to 12 months",
        transfers: "USA — only with your consent (Art. 49(1)(a) GDPR)",
      },
      marketing: {
        purpose: "CRM tracking of inquiries, campaign measurement",
        providers: "HubSpot Ireland Ltd. (EU servers js-eu1.hs-scripts.com)",
        retention: "13 months (standard HubSpot cookie hubspotutk)",
        transfers: "primarily EU; USA possible — only with your consent",
      },
      media: {
        purpose: "Display of embedded videos / maps / widgets",
        providers: "Google LLC (Fonts, Maps), LinkedIn, YouTube, Vimeo",
        retention: "depends on provider",
        transfers: "possible (e.g. USA) — only with your consent",
      },
    },
    labels: {
      purpose: "Purpose",
      providers: "Providers (examples)",
      retention: "Retention",
      transfers: "International transfers",
    },
    linksTitle: "Legal",
    legalLinks: [
      { label: "Legal Notice", to: "/impressum" },
      { label: "Privacy Policy", to: "/datenschutz" },
    ],
    note:
      "Note: Required cookies are set immediately. Everything else is only enabled after opt-in.",
    changeInFooter: "Change cookie settings",
  },
};

const Toggle = ({ checked, disabled, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
      disabled
        ? "bg-[#0052FF]/40 cursor-not-allowed"
        : checked
        ? "bg-[#0052FF]"
        : "bg-white/10 hover:bg-white/20"
    }`}
  >
    <span
      className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-200 ${
        checked ? "translate-x-[20px]" : "translate-x-0"
      }`}
    />
  </button>
);

const CategoryCard = ({ badge, badgeColor, Icon, title, body, checked, disabled, onChange, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] rounded-sm p-4">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm border ${badgeColor}`}
          >
            <Icon size={10} /> {badge}
          </span>
          <div className="font-display text-sm font-bold text-white">{title}</div>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">{body}</p>
      </div>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} testId={testId} />
    </div>
  </div>
);

const DetailBlock = ({ title, purpose, providers, retention, transfers, labels }) => (
  <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#3B82F6] mb-3">
      {title}
    </div>
    <dl className="space-y-2 text-xs text-gray-400 leading-relaxed">
      <div>
        <dt className="inline text-white font-medium">{labels.purpose}: </dt>
        <dd className="inline">{purpose}</dd>
      </div>
      <div>
        <dt className="inline text-white font-medium">{labels.providers}: </dt>
        <dd className="inline">{providers}</dd>
      </div>
      <div>
        <dt className="inline text-white font-medium">{labels.retention}: </dt>
        <dd className="inline">{retention}</dd>
      </div>
      <div>
        <dt className="inline text-white font-medium">{labels.transfers}: </dt>
        <dd className="inline">{transfers}</dd>
      </div>
    </dl>
  </div>
);

export default function CookieConsent() {
  const { lang } = useI18n() || { lang: "de" };
  const L = COPY[lang] || COPY.de;
  const [decided, setDecided] = useState(true); // start hidden to avoid SSR flash
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    media: false,
  });

  // Bootstrap on mount.
  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      setPrefs({
        necessary: true,
        analytics: !!existing.analytics,
        marketing: !!existing.marketing,
        media: !!existing.media,
      });
      applyConsent(existing);
      setDecided(true);
    } else {
      setDecided(false);
    }
  }, []);

  // Global re-open hook for the footer link.
  useEffect(() => {
    const onOpen = () => {
      const existing = readConsent();
      if (existing) {
        setPrefs({
          necessary: true,
          analytics: !!existing.analytics,
          marketing: !!existing.marketing,
          media: !!existing.media,
        });
      }
      setShowSettings(true);
      setDecided(false); // show banner (settings modal is on top)
    };
    window.addEventListener(REOPEN_EVENT, onOpen);
    return () => window.removeEventListener(REOPEN_EVENT, onOpen);
  }, []);

  // Lock body scroll while modal open.
  useEffect(() => {
    if (showSettings) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showSettings]);

  const commit = useCallback((c) => {
    const previous = readConsent();
    writeConsent(c);

    const analyticsWithdrawn = !!previous?.analytics && !c.analytics;
    const marketingWithdrawn = !!previous?.marketing && !c.marketing;
    const mediaWithdrawn = !!previous?.media && !c.media;
    const anyWithdrawn = analyticsWithdrawn || marketingWithdrawn || mediaWithdrawn;

    if (analyticsWithdrawn) {
      // Downgrade Google Consent Mode + purge GA/PostHog cookies.
      if (typeof window.gtag === "function") {
        window.gtag("consent", "update", { analytics_storage: "denied" });
      }
      purgeCookiesMatching(ANALYTICS_COOKIE_PATTERNS);
    }
    if (marketingWithdrawn) {
      purgeCookiesMatching(MARKETING_COOKIE_PATTERNS);
    }

    if (anyWithdrawn) {
      // Hard reload so any already-attached SDK (HubSpot, PostHog, GA)
      // is fully torn down. This is the only reliable way to unregister
      // third-party listeners without leaving dangling event handlers.
      setShowSettings(false);
      setDecided(true);
      setPrefs({ necessary: true, ...c });
      window.location.reload();
      return;
    }

    // No downgrade → apply consent upgrades in place (no reload needed).
    applyConsent(c);
    setPrefs({ necessary: true, ...c });
    setShowSettings(false);
    setDecided(true);
  }, []);

  const onAcceptAll = () => commit({ analytics: true, marketing: true, media: true });
  const onReject = () => commit({ analytics: false, marketing: false, media: false });
  const onSave = () =>
    commit({
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
      media: !!prefs.media,
    });

  if (decided && !showSettings) return null;

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {!decided && !showSettings && (
          <motion.div
            key="cookie-banner"
            role="dialog"
            aria-live="polite"
            aria-label={L.heading}
            data-testid="cookie-banner"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[70] bottom-4 left-4 right-4 md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-4xl"
          >
            <div className="relative overflow-hidden rounded-sm bg-[#0b1220]/95 backdrop-blur-xl border border-white/10 shadow-2xl">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#0052FF]/15 blur-3xl pointer-events-none" />
              <div className="relative p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={14} className="text-[#3B82F6]" />
                    <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#3B82F6]">
                      {L.reqBadge === "Erforderlich" ? "Cookies" : "Cookies"}
                    </span>
                  </div>
                  <div className="font-display text-base md:text-lg font-bold text-white leading-tight">
                    {L.heading}
                  </div>
                  <p className="mt-2 text-xs md:text-sm text-gray-400 leading-relaxed">
                    {L.lead}
                    <br className="hidden md:block" />
                    {L.withdraw}
                    <Link
                      to="/datenschutz"
                      className="text-[#3B82F6] hover:text-white transition-colors underline-offset-4 hover:underline"
                    >
                      {L.privacyLink}
                    </Link>
                    {L.and}
                    <Link
                      to="/impressum"
                      className="text-[#3B82F6] hover:text-white transition-colors underline-offset-4 hover:underline"
                    >
                      {L.imprintLink}
                    </Link>
                    {L.dot}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[168px]">
                  <button
                    onClick={onAcceptAll}
                    data-testid="cookie-accept-all"
                    className="bg-[#0052FF] hover:bg-[#2563EB] text-white text-xs font-medium px-4 py-2.5 rounded-sm transition-colors whitespace-nowrap"
                  >
                    {L.acceptAll}
                  </button>
                  <button
                    onClick={onReject}
                    data-testid="cookie-reject"
                    className="border border-white/15 hover:border-white/40 text-white text-xs font-medium px-4 py-2.5 rounded-sm transition-colors whitespace-nowrap"
                  >
                    {L.reject}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    data-testid="cookie-open-settings"
                    className="text-[11px] text-gray-400 hover:text-white transition-colors font-mono-tech uppercase tracking-widest py-1"
                  >
                    {L.settings}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            key="cookie-settings"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#030712]/85 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label={L.chooseWhich}
            data-testid="cookie-settings-modal"
            onClick={(e) => e.target === e.currentTarget && setShowSettings(false)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-sm bg-[#0b1220] border border-white/10 shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="relative px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
                <div className="min-w-0">
                  <div className="font-display text-lg md:text-xl font-bold text-white leading-tight">
                    {L.heading}
                  </div>
                  <p className="mt-2 text-xs md:text-sm text-gray-400 leading-relaxed max-w-3xl">
                    {L.lead} {L.withdraw}
                    <Link to="/datenschutz" className="text-[#3B82F6] hover:text-white transition-colors">
                      {L.privacyLink}
                    </Link>
                    {L.and}
                    <Link to="/impressum" className="text-[#3B82F6] hover:text-white transition-colors">
                      {L.imprintLink}
                    </Link>
                    {L.dot}
                  </p>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  aria-label={L.close}
                  data-testid="cookie-settings-close"
                  className="w-8 h-8 rounded-sm border border-white/10 hover:border-white/40 hover:bg-white/5 flex items-center justify-center text-gray-400 hover:text-white transition-colors shrink-0"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Left: category toggles */}
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-4">
                    {L.chooseWhich}
                  </div>
                  <div className="space-y-3">
                    <CategoryCard
                      badge={L.reqBadge}
                      badgeColor="bg-white/5 text-gray-300 border-white/15"
                      Icon={Shield}
                      title={L.cats.necessary.title}
                      body={L.cats.necessary.body}
                      checked
                      disabled
                      onChange={() => {}}
                      testId="cookie-toggle-necessary"
                    />
                    <CategoryCard
                      badge={L.analyticsBadge}
                      badgeColor="bg-[#3B82F6]/10 text-[#93C5FD] border-[#3B82F6]/30"
                      Icon={BarChart3}
                      title={L.cats.analytics.title}
                      body={L.cats.analytics.body}
                      checked={prefs.analytics}
                      onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                      testId="cookie-toggle-analytics"
                    />
                    <CategoryCard
                      badge={L.marketingBadge}
                      badgeColor="bg-[#10B981]/10 text-[#6EE7B7] border-[#10B981]/30"
                      Icon={Megaphone}
                      title={L.cats.marketing.title}
                      body={L.cats.marketing.body}
                      checked={prefs.marketing}
                      onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                      testId="cookie-toggle-marketing"
                    />
                    <CategoryCard
                      badge={L.mediaBadge}
                      badgeColor="bg-[#F59E0B]/10 text-[#FCD34D] border-[#F59E0B]/30"
                      Icon={Film}
                      title={L.cats.media.title}
                      body={L.cats.media.body}
                      checked={prefs.media}
                      onChange={(v) => setPrefs((p) => ({ ...p, media: v }))}
                      testId="cookie-toggle-media"
                    />
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    <button
                      onClick={onSave}
                      data-testid="cookie-save"
                      className="bg-[#0052FF] hover:bg-[#2563EB] text-white text-xs font-medium px-4 py-2.5 rounded-sm transition-colors"
                    >
                      {L.save}
                    </button>
                    <button
                      onClick={onAcceptAll}
                      data-testid="cookie-accept-all-modal"
                      className="border border-white/15 hover:border-[#0052FF] hover:bg-[#0052FF]/10 text-white text-xs font-medium px-4 py-2.5 rounded-sm transition-colors"
                    >
                      {L.acceptAll}
                    </button>
                    <button
                      onClick={onReject}
                      data-testid="cookie-reject-modal"
                      className="border border-white/15 hover:border-white/40 text-white text-xs font-medium px-4 py-2.5 rounded-sm transition-colors"
                    >
                      {L.reject}
                    </button>
                  </div>
                  <p className="mt-4 text-[11px] text-gray-400 leading-relaxed">
                    {L.changeAnytime}
                  </p>
                </div>

                {/* Right: clear & simple details */}
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-4">
                    {L.clearSimple}
                  </div>
                  <div className="space-y-3">
                    <DetailBlock
                      title={L.analyticsBadge}
                      purpose={L.details.analytics.purpose}
                      providers={L.details.analytics.providers}
                      retention={L.details.analytics.retention}
                      transfers={L.details.analytics.transfers}
                      labels={L.labels}
                    />
                    <DetailBlock
                      title={L.marketingBadge}
                      purpose={L.details.marketing.purpose}
                      providers={L.details.marketing.providers}
                      retention={L.details.marketing.retention}
                      transfers={L.details.marketing.transfers}
                      labels={L.labels}
                    />
                    <DetailBlock
                      title={L.mediaBadge}
                      purpose={L.details.media.purpose}
                      providers={L.details.media.providers}
                      retention={L.details.media.retention}
                      transfers={L.details.media.transfers}
                      labels={L.labels}
                    />
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 text-xs">
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-2">
                      {L.linksTitle}
                    </div>
                    <ul className="space-y-1.5">
                      {L.legalLinks.map((l) => (
                        <li key={l.to}>
                          <Link
                            to={l.to}
                            className="text-[#3B82F6] hover:text-white transition-colors"
                            onClick={() => setShowSettings(false)}
                          >
                            · {l.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-[11px] text-gray-400 leading-relaxed">
                      {L.note}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
